import Button, { type ButtonProps } from 'components/UI/CoreUIButton'
import { useCollapse } from 'hooks/collapse'
import type * as React from 'react'
import { useNavigate } from 'react-router-dom'
import SectionTitle from './SectionTitle'

interface SectionProps {
  title?: React.ReactNode
  titleExtra?: React.ReactNode
  options?: React.ReactElement | null
  collapse?: boolean
  children: React.ReactNode
  withBackBtn?: boolean | string
  backButtonProps?: ButtonProps
  className?: string
  titleClassName?: string
  contentClassName?: string
  floatingBackBtn?: boolean
  titleSize?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'
}

export default function Section({
  title = '',
  titleExtra = null,
  options = null,
  collapse = false,
  children,
  withBackBtn = false,
  backButtonProps,
  className = '',
  titleClassName = '',
  titleSize = '3xl',
  contentClassName = '',
}: SectionProps) {
  const { Icon, open } = useCollapse()
  const navigate = useNavigate()

  const back = typeof withBackBtn === 'string' ? () => navigate(withBackBtn) : () => navigate(-1)

  const collapseCss = collapse ? (open ? 'opacity-100 mt-2' : 'max-h-0 opacity-0 mt-0') : title ? 'mt-4' : 'mt-2'

  return (
    <div
      className={`
        bg-white border border-gray-200 rounded-xxs px-8 py-6
        flex flex-col w-full relative
        ${className || ''}
      `}
    >
      <div className="flex justify-between items-center w-full h-fit">
        <SectionTitle size={titleSize} className={`h-fit flex items-center gap-2 ${titleClassName || ''}`}>
          {withBackBtn && (
            <Button
              {...backButtonProps}
              data-testid="back-button"
              variant={backButtonProps?.variant ?? 'secondary'}
              styles={{
                root: {
                  position: 'absolute',
                  top: '2rem',
                  left: '-1.25rem',
                  ...(typeof backButtonProps?.styles !== 'function' ? backButtonProps?.styles?.root : {}),
                },
              }}
              onClick={backButtonProps?.onClick ?? back}
              singleIcon={backButtonProps?.singleIcon ?? 'chevron-left'}
            />
          )}
          {title}
          {titleExtra}
        </SectionTitle>
        {collapse ? <Icon className="ml-2" /> : null}
        <div className="grow" />
        <div className="flex items-center">{options}</div>
      </div>
      <div className={`transition-all duration-300 ${collapseCss} ${contentClassName || ''}`}>{children}</div>
    </div>
  )
}
